import { useEffect, useMemo, useState } from 'react';

/**
 * Custom hook to manage form state and handle form submission.
 *
 * @param {Object} options - Options for the useForm hook.
 * @param {Function} options.onSubmit - Callback function to handle form submission.
 * @returns {Object} - Returns an object containing form data, submission state, and handlers.
 * @returns {Object} formData - The current state of the form data.
 * @returns {boolean} isSubmitted - Indicates whether the form has been submitted.
 * @returns {Function} handleInputChange - Handler function to update form data on input change.
 * @returns {Function} handleSubmit - Handler function to manage form submission.
 */
const useForm = ({ onSubmit, initialValues = {}, currentRequestType = "", name = null, value = null }) => {
    const memoizedInitialValues = useMemo(() => initialValues, [initialValues]);

    const [formData, setFormData] = useState(memoizedInitialValues);

    const [isSubmitted, setIsSubmitted] = useState(false);
    useEffect(() => {
        if (Object.keys(initialValues).length > 0) setFormData(initialValues);
    }, [initialValues]);

    /**
     * Handles input change events and updates the form data state.
     *
     * @param {Event} e - The input change event.
     */
    const handleInputChange = (e, name = null, value = null) => {
        if (e && e.target) {
            setFormData(formData => ({ ...formData, [e.target.name]: e.target.value }));
        } else if (name && value !== null) {
            setFormData(formData => ({ ...formData, [name]: value }));
        }
        isSubmitted && setIsSubmitted(false);
    };

    /**
     * Handles form submission events.
     *
     * @param {Event} e - The form submission event.
     */
    const handleSubmit = (e, id) => {
        e.preventDefault();
        onSubmit && onSubmit(e, id);
        setIsSubmitted(true);
    };

    const clearInput = (name) => {
        setFormData(formData => ({ ...formData, [name]: "" }));
    };

    const clearFormData = () => {
        setFormData({});
    };

    return {
        formData,
        isSubmitted,
        handleInputChange,
        handleSubmit,
        clearInput,
        clearFormData
    };
};

export { useForm };

